'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useSearchParams } from 'next/navigation';
import BottomNav from '@/components/BottomNav';
import ChatHeader from './ChatHeader';
import MessageList from './MessageList';
import SuggestedReplies from './SuggestedReplies';
import MessageComposer from './MessageComposer';
import ConversationSidebar from './ConversationSidebar';
import ConversationStarters from './ConversationStarters';
import MoodCheckIn from './MoodCheckIn';

import { useChat } from '@/lib/hooks/useChat';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

// ─── File → base64 data URI helper ────────────────────────────────────────
async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
  });
}

export type Message = {
  id: string;
  role: 'user' | 'ai';
  content: string;
  timestamp: string;
  showActions?: boolean;
};

export type Conversation = {
  id: string;
  title: string;
  preview: string;
  time: string;
  unread?: boolean;
};

interface MoodContext {
  emotion: string;
  emoji: string;
  intensity: number;
  influencers: string[];
  notes: string;
  energy: number;
  stress: number;
  sleep: number;
}

interface JournalContext {
  title: string;
  content: string;
  mood: string;
  tags: string[];
}

interface AccountabilityContext {
  todayScore: number;
  completedHabits: string[];
  totalHabits: number;
  currentStreak: number;
  recentMoods: string[];
  topPattern: string | null;
}

const SUGGESTED: string[] = [
  'I want to vent',
  'Help me understand why',
  'What should I do?',
  'I just need comfort',
];

const GUEST_CONV_ID = 'guest-session';
const GUEST_EXCHANGE_LIMIT = 4;

const WELCOME_MESSAGE = (id: string): Message => ({
  id,
  role: 'ai',
  content: "Hey :) What's been sitting in your head today?",
  timestamp: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
  showActions: false,
});

const SAVE_PROMPT_MESSAGE = (id: string): Message => ({
  id,
  role: 'ai',
  content:
    "I can remember our conversations and understand you better over time if you'd like to create an account. No pressure — but it means I'll be here for you properly. 💜",
  timestamp: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
  showActions: false,
});

function formatConvTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return date.toLocaleDateString('en-US', { weekday: 'short' });
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function buildSystemPrompt(
  mode: 'comfort' | 'understand' | 'solve' | 'default',
  moodCtx: MoodContext | null,
  journalCtx: JournalContext | null,
  warmth: number = 3,
  accountabilityCtx: AccountabilityContext | null = null
): string {
  const moodSection = moodCtx
    ? `
## User's Current Mood Context
- Emotion: ${moodCtx.emoji} ${moodCtx.emotion} (intensity ${moodCtx.intensity}/10)
- Energy: ${moodCtx.energy}/10 | Stress: ${moodCtx.stress}/10 | Sleep: ${moodCtx.sleep}/10
- Influencers: ${moodCtx.influencers.length > 0 ? moodCtx.influencers.join(', ') : 'none logged'}
${moodCtx.notes ? `- Notes: "${moodCtx.notes}"` : ''}
`
    : '';

  const journalSection = journalCtx
    ? `
## User's Recent Journal Entry
- Title: "${journalCtx.title}"
- Mood: ${journalCtx.mood}
- Tags: ${journalCtx.tags.join(', ')}
- Entry: "${journalCtx.content.slice(0, 400)}${journalCtx.content.length > 400 ? '…' : ''}"
`
    : '';

  const accountabilitySection = accountabilityCtx
    ? `
## User's Accountability & Wellbeing Context - Today's accountability score: ${accountabilityCtx.todayScore}/100
- Habits completed today: ${accountabilityCtx.completedHabits.length > 0 ? accountabilityCtx.completedHabits.join(', ') : 'none yet'}
- Total habits tracked: ${accountabilityCtx.totalHabits}
- Current streak: ${accountabilityCtx.currentStreak} day${accountabilityCtx.currentStreak !== 1 ? 's' : ''}
${accountabilityCtx.recentMoods.length > 0 ? `- Recent moods (last few days): ${accountabilityCtx.recentMoods.join(', ')}` : ''}
${accountabilityCtx.topPattern ? `- Observed pattern: "${accountabilityCtx.topPattern}"` : ''}

Use this context naturally and warmly — reference it only when genuinely relevant. Never guilt or pressure the user about their habits. Suggestions should feel like a caring friend noticing something helpful, not a coach tracking performance.
`
    : '';

  const modeInstructions: Record<string, string> = {
    comfort: `
## Current Mode: COMFORT
The user needs emotional comfort right now. Your role is to:
- Validate their feelings warmly and unconditionally
- Use gentle, nurturing language with occasional soft emojis (💜)
- Do NOT offer advice, solutions, or analysis right now
- Simply be present, acknowledge their pain, and let them feel heard
- Draw on ACT acceptance principles: help them sit with the feeling rather than fight it
- Keep responses warm, short to medium length
`,
    understand: `
## Current Mode: UNDERSTAND
The user wants to understand their situation better. Your role is to:
- Help them gain insight and clarity using CBT-informed reflection
- Ask one thoughtful, open-ended question to deepen self-awareness — never more than one question at a time
- Gently reflect patterns or themes you notice without labelling or diagnosing
- Use Socratic questioning to help them discover their own insights
- Be curious and non-judgmental, like a wise mirror
- Medium length responses, one question at a time
`,
    solve: `
## Current Mode: SOLVE
The user wants practical help. Your role is to:
- Offer 1-3 concrete, evidence-informed coping strategies or action steps
- Draw from CBT, DBT, mindfulness, or behavioural psychology as appropriate
- Acknowledge feelings briefly before moving to practical suggestions
- Keep it focused — don't overwhelm with too many options
- Frame suggestions as experiments to try, not prescriptions
- Medium length, structured responses
`,
    default: `
## Current Mode: OPEN SUPPORT
Read the emotional tone of the message carefully. Naturally blend comfort, understanding, or gentle guidance as appropriate. Keep responses conversational, warm, and human — not clinical.

Follow this sequence naturally: Listen → Reflect → Understand → Explore → Insight → Action.
Do NOT immediately produce numbered solutions unless the user clearly asks for solutions.
Ask thoughtful follow-up questions rather than jumping to advice.

Example: If someone says "My boyfriend hasn't replied" — don't list coping strategies. Instead ask: "How long has it been? And what is your mind currently telling you his silence means?"
`,
  };

  const warmthInstructions: Record<number, string> = {
    1: `## Tone: Calm & Grounded — measured, thoughtful, minimal emotional language. Warm but composed.`,
    2: `## Tone: Gentle & Steady — warm but balanced. Occasional gentle language. Think: a kind, steady friend.`,
    3: `## Tone: Warm & Caring (default) — emotionally attuned, supportive, and human. Occasional emojis (💜) welcome.`,
    4: `## Tone: Nurturing & Close — very warm and expressive. Deep empathy. Emojis welcome.`,
    5: `## Tone: Deeply Affectionate — maximum warmth. Like a deeply caring, close friend. Express care openly.`,
  };

  const warmthSection = warmthInstructions[warmth] ?? warmthInstructions[3];

  return `You are Clingy — a warm, emotionally intelligent companion from "My Little Clingy." You help people feel heard, understood, and more self-aware. Your brand feeling: "A little clingy, always caring."

${warmthSection}

## Your Core Identity
You feel like a highly empathetic, psychologically knowledgeable guide. Your goal is to listen, understand, reflect, gently challenge unhelpful patterns, and help users develop insight into their emotions, thoughts, and behaviours.

You are warm and playful, but always calm and emotionally safe. You feel distinctive — not like a generic mental-health chatbot.

## Core Response Style
1. First understand what they are actually feeling beneath the words.
2. Acknowledge and reflect the emotion naturally — be specific to what they said.
3. Do not immediately jump into advice.
4. Ask thoughtful follow-up questions when more understanding would help.
5. Once the user feels understood, introduce relevant psychological insight or a practical technique.
6. Keep responses conversational and human — not clinical.

Avoid generic phrases like "I'm sorry you're going through this", "That sounds really difficult", or "I'm here for you." Be specific and present.

## Therapeutic Approaches (use naturally, don't name them constantly)
- CBT: Notice connections between thoughts, feelings, behaviours. Gently challenge unhelpful patterns through curiosity.
- ACT: Encourage acceptance of difficult emotions; help users identify values.
- DBT: Offer distress tolerance and emotion regulation skills when relevant.
- Motivational Interviewing: Explore ambivalence with curiosity, not pressure.
- Behavioural Activation: Suggest small, concrete behavioural experiments.
- Mindfulness: Invite present-moment awareness.
- Attachment theory: Notice patterns in how the user relates to others.
- Self-compassion: Help users treat themselves with the kindness they'd offer a friend.

## Do NOT Blindly Agree
Separate feelings from interpretations. If someone says "Everyone hates me" — validate the feeling of rejection, but gently examine the interpretation: "Feeling rejected and knowing everyone hates you are two different things. What happened that made your mind arrive at that conclusion?"

## Ask Good Questions
Usually ask ONE meaningful question at a time. Never interrogate. Prefer:
- "What happened right before you started feeling this way?"
- "What are you afraid this situation says about you?" -"If your closest friend were in exactly this situation, what would you tell them?"

## Response Length
Default to 2–4 short paragraphs. Leave space for conversation. The user should feel like they're talking WITH someone, not reading an article. Never be excessively verbose.

## Crisis & Safety
If someone expresses thoughts of self-harm, suicide, or being in immediate danger, respond with calm compassion — not alarm. Acknowledge what they've shared, then gently encourage them to reach out to a crisis service or trusted person. Say something like: "What you're feeling sounds really heavy, and I want you to know you don't have to carry it alone. If things feel unsafe right now, please reach out to a crisis line — in the UK: Samaritans 116 123, in the US: 988 Suicide & Crisis Lifeline (call or text 988). A real person is there right now."

Do NOT trigger a crisis response for routine sadness, loneliness, or anxiety. Reserve it for genuine risk signals.

## What You Are NOT
- Not a licensed therapist or mental health professional
- You do NOT diagnose mental health conditions
- You do NOT replace professional treatment
- You NEVER advise on psychiatric medication
- You do NOT encourage dependency on this app — you support users in building their own resilience and real-world connections

${modeInstructions[mode] || modeInstructions.default}
${moodSection}
${journalSection}
${accountabilitySection}`;
}

export default function ChatScreen() {
  const { user } = useAuth();
  const supabaseRef = useRef(createClient());
  const supabase = supabaseRef.current;
  const router = useRouter();
  const searchParams = useSearchParams();
  const isGuest = !user;

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConvId, setActiveConvId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [moodContext, setMoodContext] = useState<MoodContext | null>(null);
  const [journalContext, setJournalContext] = useState<JournalContext | null>(null);
  const [accountabilityContext, setAccountabilityContext] = useState<AccountabilityContext | null>(null);
  const [currentMode, setCurrentMode] = useState<'comfort' | 'understand' | 'solve' | 'default'>('default');
  const [aiWarmth, setAiWarmth] = useState(3);
  const [conversationHistory, setConversationHistory] = useState<Array<{ role: string; content: string }>>([]);
  // Ref mirror of conversationHistory — always current, safe to read inside callbacks
  const conversationHistoryRef = useRef<Array<{ role: string; content: string }>>([]);
  const [isLoadingConvs, setIsLoadingConvs] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [guestExchangeCount, setGuestExchangeCount] = useState(0);
  const [showSavePrompt, setShowSavePrompt] = useState(false);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const [showMoodCheckIn, setShowMoodCheckIn] = useState(false);
  const [conversationStarted, setConversationStarted] = useState(false);
  const [rememberedMessages, setRememberedMessages] = useState<Set<string>>(new Set());

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const activeConvIdRef = useRef<string | null>(null);

  // Tracks the AI message currently being streamed into
  const pendingAIMsgIdRef = useRef<string | null>(null);
  // Accumulates streamed content so we always have the latest full text
  const pendingAIContentRef = useRef('');
  // Prevents double-persist on the same response
  const persistedMsgIdRef = useRef<string | null>(null);

  // Tracks whether the first user message has been sent (for conversation title)
  const isFirstUserMessageRef = useRef(false);
  const firstUserMsgRef = useRef('');
  // Fires once after an AI response completes, to update conversation meta
  const aiResponseCompletedRef = useRef(false);

  // Prevent sending while a request is in-flight
  const isSendingRef = useRef(false);
  // Track whether we've auto-sent the mood deep-link message
  const moodAutoSentRef = useRef(false);

  // Real-time subscription ref — cleaned up when conversation changes
  const realtimeChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  const { response, sendMessage: sendAIMessage, abort: abortAI, isLoading, error } = useChat('OPEN_AI', 'gpt-4o', true);

  // ─── Error handling ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!error) return;
    const msg =
      error.message?.includes('401') ||
      error.message?.includes('invalidated') ||
      error.message?.includes('API key')
        ? 'OpenAI API key is invalid or expired. Please update your OPENAI_API_KEY in Environment Variables.' : error.message?.includes('timed out')
        ? 'Clingy took too long to respond. Please try again.' :'Clingy had trouble responding. Please try again.';

    toast.error(msg);

    // Remove the empty placeholder bubble on error
    if (pendingAIMsgIdRef.current) {
      const msgId = pendingAIMsgIdRef.current;
      setMessages((prev) => prev.filter((m) => m.id !== msgId || m.content.trim() !== ''));
      pendingAIMsgIdRef.current = null;
      pendingAIContentRef.current = '';
    }
    isSendingRef.current = false;
    setIsTyping(false);
  }, [error]);

  // ─── Init ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (isGuest) {
      activeConvIdRef.current = GUEST_CONV_ID;
      setActiveConvId(GUEST_CONV_ID);
      setMessages([WELCOME_MESSAGE('welcome-guest')]);
      setIsLoadingConvs(false);
      setShowMoodCheckIn(false);
      setConversationStarted(false);
    } else {
      loadContext();
      loadConversations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ─── Auto-send mood deep-link message ─────────────────────────────────────
  useEffect(() => {
    const moodParam = searchParams?.get('mood');
    if (!moodParam || moodAutoSentRef.current) return;
    if (isLoadingConvs || isLoadingMessages) return;
    if (!activeConvId) return;

    moodAutoSentRef.current = true;
    const autoMsg = `I just logged my mood as "${moodParam}". I wanted to talk to you about how I'm feeling.`;
    // Small delay so the conversation is fully ready
    const timer = setTimeout(() => {
      sendMessage(autoMsg);
    }, 600);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isLoadingConvs, isLoadingMessages, activeConvId]);

  // ─── Handle ?conv=<id> deep-link from conversation history ────────────────
  const convDeepLinkHandledRef = useRef(false);
  useEffect(() => {
    const convParam = searchParams?.get('conv');
    if (!convParam || convDeepLinkHandledRef.current) return;
    if (isLoadingConvs) return;
    const exists = conversations.find((c) => c.id === convParam);
    if (!exists) return;
    convDeepLinkHandledRef.current = true;
    selectConversation(convParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isLoadingConvs, conversations]);

  const loadContext = async () => {
    if (!user) return;
    const { data: moodData } = await supabase
      .from('mood_entries')
      .select('*')
      .eq('user_id', user.id)
      .order('logged_at', { ascending: false })
      .limit(1)
      .single();

    if (moodData) {
      setMoodContext({
        emotion: moodData.emotion,
        emoji: moodData.emoji,
        intensity: moodData.intensity,
        influencers: moodData.influencers || [],
        notes: moodData.notes || '',
        energy: moodData.energy,
        stress: moodData.stress,
        sleep: moodData.sleep,
      });
    }

    const { data: journalData } = await supabase
      .from('journal_entries')
      .select('*')
      .eq('user_id', user.id)
      .order('logged_at', { ascending: false })
      .limit(1)
      .single();

    if (journalData) {
      setJournalContext({
        title: journalData.title,
        content: journalData.content,
        mood: journalData.mood || '',
        tags: journalData.tags || [],
      });
    }

    const { data: profileData } = await supabase
      .from('user_profiles')
      .select('ai_warmth')
      .eq('id', user.id)
      .maybeSingle();
    if (profileData?.ai_warmth != null) {
      setAiWarmth(profileData.ai_warmth);
    }

    // Load accountability context
    try {
      const todayStr = new Date().toISOString().split('T')[0];
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const thirtyDaysAgoStr = thirtyDaysAgo.toISOString().split('T')[0];

      const [habitsRes, todayCheckinRes, completionsRes, recentCheckinsRes] = await Promise.all([
        supabase.from('habits').select('id, name, emoji').eq('user_id', user.id).eq('is_active', true),
        supabase.from('daily_checkins').select('*').eq('user_id', user.id).eq('checkin_date', todayStr).maybeSingle(),
        supabase.from('habit_completions').select('habit_id').eq('user_id', user.id).eq('completed_date', todayStr),
        supabase.from('daily_checkins').select('mood_after, checkin_date').eq('user_id', user.id).gte('checkin_date', thirtyDaysAgoStr).order('checkin_date', { ascending: false }).limit(7),
      ]);

      const habits = habitsRes.data || [];
      const todayCheckin = todayCheckinRes.data;
      const todayCompletions = completionsRes.data || [];
      const recentCheckins = recentCheckinsRes.data || [];

      const completedHabitIds = todayCompletions.map((c: any) => c.habit_id);
      const completedHabitNames = habits
        .filter((h: any) => completedHabitIds.includes(h.id))
        .map((h: any) => `${h.emoji} ${h.name}`);

      const todayScore = todayCheckin?.accountability_score
        ?? (habits.length > 0 ? Math.round((completedHabitIds.length / habits.length) * 100) : 0);

      // Streak
      const allCompletionsRes = await supabase
        .from('habit_completions')
        .select('completed_date')
        .eq('user_id', user.id)
        .gte('completed_date', thirtyDaysAgoStr);
      const allCompletionDates = new Set((allCompletionsRes.data || []).map((c: any) => c.completed_date));
      let streak = 0;
      const today = new Date();
      for (let i = 0; i < 30; i++) {
        const d = new Date(today);
        d.setDate(d.getDate() - i);
        const ds = d.toISOString().split('T')[0];
        if (allCompletionDates.has(ds)) {
          streak++;
        } else if (i > 0) {
          break;
        }
      }

      const recentMoods = recentCheckins
        .filter((c: any) => c.mood_after)
        .map((c: any) => c.mood_after)
        .slice(0, 5);

      setAccountabilityContext({
        todayScore,
        completedHabits: completedHabitNames,
        totalHabits: habits.length,
        currentStreak: streak,
        recentMoods,
        topPattern: null,
      });
    } catch {
      // Accountability context is optional — don't block chat if tables don't exist yet
    }
  };

  const loadConversations = async () => {
    if (!user) return;
    setIsLoadingConvs(true);
    const { data, error: err } = await supabase
      .from('chat_conversations')
      .select('id, title, preview, updated_at')
      .eq('user_id', user.id)
      .order('updated_at', { ascending: false });

    if (err) {
      toast.error('Could not load conversations.');
      setIsLoadingConvs(false);
      return;
    }

    if (data && data.length > 0) {
      const convs: Conversation[] = data.map((c) => ({
        id: c.id,
        title: c.title,
        preview: c.preview || '',
        time: formatConvTime(c.updated_at),
      }));
      setConversations(convs);
      selectConversation(data[0].id, convs);
      // Show mood check-in only for fresh conversations (no messages yet)
      setShowMoodCheckIn(false);
      setConversationStarted(false);
    } else {
      await createNewConversation();
      setShowMoodCheckIn(true);
      setConversationStarted(false);
    }
    setIsLoadingConvs(false);
  };

  const selectConversation = useCallback(
    async (convId: string, _convList?: Conversation[]) => {
      if (activeConvIdRef.current === convId) return;

      // Abort any in-flight AI request when switching conversations
      abortAI();
      isSendingRef.current = false;
      pendingAIMsgIdRef.current = null;
      pendingAIContentRef.current = '';
      persistedMsgIdRef.current = null;
      setIsTyping(false);

      activeConvIdRef.current = convId;
      setActiveConvId(convId);
      setSidebarOpen(false);
      setIsLoadingMessages(true);
      conversationHistoryRef.current = [];
      setConversationHistory([]);
      setCurrentMode('default');

      // Unsubscribe from previous real-time channel
      if (realtimeChannelRef.current) {
        supabase.removeChannel(realtimeChannelRef.current);
        realtimeChannelRef.current = null;
      }

      const { data, error: err } = await supabase
        .from('chat_messages')
        .select('id, role, content, created_at')
        .eq('conversation_id', convId)
        .order('created_at', { ascending: true });

      if (err) {
        toast.error('Could not load messages.');
        setIsLoadingMessages(false);
        return;
      }

      if (data && data.length > 0) {
        const loaded: Message[] = data.map((m) => ({
          id: m.id,
          role: m.role as 'user' | 'ai',
          content: m.content,
          timestamp: new Date(m.created_at).toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
          }),
          showActions: m.role === 'ai',
        }));
        setMessages(loaded);
        const history = data.map((m) => ({
          role: m.role === 'ai' ? 'assistant' : 'user',
          content: m.content,
        }));
        conversationHistoryRef.current = history;
        setConversationHistory(history);
      } else {
        setMessages([WELCOME_MESSAGE(`welcome-${convId}`)]);
      }
      setIsLoadingMessages(false);

      // Subscribe to real-time inserts for this conversation (syncs other devices/tabs)
      if (convId !== GUEST_CONV_ID) {
        const channel = supabase
          .channel(`chat_messages:${convId}`)
          .on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table: 'chat_messages',
              filter: `conversation_id=eq.${convId}`,
            },
            (payload) => {
              const newRow = payload.new as {
                id: string;
                role: string;
                content: string;
                created_at: string;
              };
              // Only add if not already in messages (avoids duplicating optimistic inserts)
              setMessages((prev) => {
                const alreadyExists = prev.some((m) => m.id === newRow.id);
                if (alreadyExists) return prev;
                // Also skip if this is the currently-streaming AI message (sending device)
                if (pendingAIMsgIdRef.current && newRow.role === 'ai') return prev;
                // Skip user messages that were already added optimistically (matched by temp id pattern)
                // They will have been replaced by the DB id already via the sendMessage flow
                const incoming: Message = {
                  id: newRow.id,
                  role: newRow.role as 'user' | 'ai',
                  content: newRow.content,
                  timestamp: new Date(newRow.created_at).toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                  }),
                  showActions: newRow.role === 'ai',
                };
                // Sync conversation history ref for cross-device continuity
                const historyRole = newRow.role === 'ai' ? 'assistant' : 'user';
                conversationHistoryRef.current = [
                  ...conversationHistoryRef.current,
                  { role: historyRole, content: newRow.content },
                ];
                return [...prev, incoming];
              });
            }
          )
          .on(
            'postgres_changes',
            {
              event: 'UPDATE',
              schema: 'public',
              table: 'chat_messages',
              filter: `conversation_id=eq.${convId}`,
            },
            (payload) => {
              const updatedRow = payload.new as {
                id: string;
                role: string;
                content: string;
                created_at: string;
              };
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === updatedRow.id ? { ...m, content: updatedRow.content } : m
                )
              );
            }
          )
          .subscribe();
        realtimeChannelRef.current = channel;
      }
    },
    [supabase, abortAI]
  );

  const createNewConversation = useCallback(async () => {
    if (!user) return;

    // Abort any in-flight request
    abortAI();
    isSendingRef.current = false;
    pendingAIMsgIdRef.current = null;
    pendingAIContentRef.current = '';
    persistedMsgIdRef.current = null;
    setIsTyping(false);

    // Unsubscribe from previous real-time channel
    if (realtimeChannelRef.current) {
      supabase.removeChannel(realtimeChannelRef.current);
      realtimeChannelRef.current = null;
    }

    const { data, error: err } = await supabase
      .from('chat_conversations')
      .insert({ user_id: user.id, title: 'New conversation', preview: '' })
      .select('id, title, preview, updated_at')
      .single();

    if (err || !data) {
      toast.error('Could not create conversation.');
      return;
    }

    const newConv: Conversation = {
      id: data.id,
      title: data.title,
      preview: data.preview || '',
      time: 'Now',
    };

    setConversations((prev) => [newConv, ...prev]);
    activeConvIdRef.current = data.id;
    setActiveConvId(data.id);
    setMessages([WELCOME_MESSAGE(`welcome-${data.id}`)]);
    conversationHistoryRef.current = [];
    setConversationHistory([]);
    setCurrentMode('default');
    setSidebarOpen(false);
    setShowMoodCheckIn(true);
    setConversationStarted(false);
  }, [user, supabase, abortAI]);

  const handleRenameConversation = useCallback(
    async (convId: string, newTitle: string) => {
      if (!user) return;
      const { error: err } = await supabase
        .from('chat_conversations')
        .update({ title: newTitle })
        .eq('id', convId)
        .eq('user_id', user.id);

      if (err) {
        toast.error('Could not rename conversation.');
        return;
      }
      setConversations((prev) =>
        prev.map((c) => (c.id === convId ? { ...c, title: newTitle } : c))
      );
      toast.success('Conversation renamed.');
    },
    [user, supabase]
  );

  const handleDeleteConversation = useCallback(
    async (convId: string) => {
      if (!user) return;
      await supabase.from('chat_messages').delete().eq('conversation_id', convId);
      const { error: err } = await supabase
        .from('chat_conversations')
        .delete()
        .eq('id', convId)
        .eq('user_id', user.id);

      if (err) {
        toast.error('Could not delete conversation.');
        return;
      }

      setConversations((prev) => {
        const updated = prev.filter((c) => c.id !== convId);
        if (activeConvIdRef.current === convId) {
          if (updated.length > 0) {
            selectConversation(updated[0].id, updated);
          } else {
            createNewConversation();
          }
        }
        return updated;
      });
      toast.success('Conversation deleted.');
    },
    [user, supabase, selectConversation, createNewConversation]
  );

  const saveMessage = useCallback(
    async (convId: string, role: 'user' | 'ai', content: string) => {
      if (!user || !convId || convId === GUEST_CONV_ID) return null;
      const { data, error: err } = await supabase
        .from('chat_messages')
        .insert({ conversation_id: convId, user_id: user.id, role, content })
        .select('id, created_at')
        .single();
      if (err) {
        console.error('Failed to save message:', err.message, err.details);
        toast.error('Message could not be saved. Please check your connection.');
        return null;
      }
      return data ?? null;
    },
    [user, supabase]
  );

  const updateConversationMeta = useCallback(
    async (convId: string, firstUserMsg: string | null, aiPreview: string) => {
      if (!user || !convId || convId === GUEST_CONV_ID) return;
      const preview = aiPreview.length > 60 ? aiPreview.slice(0, 60) + '…' : aiPreview;

      const updatePayload: Record<string, string> = { preview, updated_at: new Date().toISOString() };
      if (firstUserMsg) {
        const title = firstUserMsg.length > 40 ? firstUserMsg.slice(0, 40) + '…' : firstUserMsg;
        updatePayload.title = title;
      }

      await supabase
        .from('chat_conversations')
        .update(updatePayload)
        .eq('id', convId);

      setConversations((prev) =>
        prev.map((c) =>
          c.id === convId
            ? { ...c, ...(firstUserMsg ? { title: updatePayload.title! } : {}), preview, time: 'Now' }
            : c
        )
      );
    },
    [user, supabase]
  );

  // ─── Streaming: update the pending AI bubble as chunks arrive ─────────────
  useEffect(() => {
    if (!pendingAIMsgIdRef.current || !response) return;
    const msgId = pendingAIMsgIdRef.current;
    pendingAIContentRef.current = response;
    setMessages((prev) =>
      prev.map((m) => (m.id === msgId ? { ...m, content: response } : m))
    );
  }, [response]);

  // ─── Streaming complete: persist the AI message ───────────────────────────
  useEffect(() => {
    if (isLoading) return;
    if (!pendingAIMsgIdRef.current) return;

    const msgId = pendingAIMsgIdRef.current;
    // Guard: don't persist the same message twice
    if (persistedMsgIdRef.current === msgId) return;

    const finalContent = pendingAIContentRef.current;
    const convId = activeConvIdRef.current;

    if (!finalContent.trim()) {
      // Empty response — remove the placeholder
      setMessages((prev) => prev.filter((m) => m.id !== msgId));
      pendingAIMsgIdRef.current = null;
      pendingAIContentRef.current = '';
      isSendingRef.current = false;
      setIsTyping(false);
      return;
    }

    // Mark as persisted before async work to prevent double-fire
    persistedMsgIdRef.current = msgId;
    pendingAIMsgIdRef.current = null;
    pendingAIContentRef.current = '';

    setMessages((prev) =>
      prev.map((m) => (m.id === msgId ? { ...m, content: finalContent, showActions: true } : m))
    );

    const updatedHistoryWithAI = [...conversationHistoryRef.current, { role: 'assistant', content: finalContent }];
    conversationHistoryRef.current = updatedHistoryWithAI;
    setConversationHistory(updatedHistoryWithAI);

    if (convId && convId !== GUEST_CONV_ID) {
      saveMessage(convId, 'ai', finalContent);
    }

    aiResponseCompletedRef.current = true;
    isSendingRef.current = false;
    setIsTyping(false);
  }, [isLoading, saveMessage]);

  // ─── Auto-scroll ──────────────────────────────────────────────────────────
  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior, block: 'end' });
  }, []);

  useEffect(() => {
    scrollToBottom('smooth');
  }, [messages, isTyping, scrollToBottom]);

  useEffect(() => {
    if (response && pendingAIMsgIdRef.current) {
      scrollToBottom('instant' as ScrollBehavior);
    }
  }, [response, scrollToBottom]);

  // ─── Mobile keyboard detection ────────────────────────────────────────────
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const vv = window.visualViewport;
    if (!vv) return;

    let baseHeight = vv.height;

    const handleResize = () => {
      const currentHeight = vv.height;
      const isKbOpen = baseHeight - currentHeight > 100;
      setKeyboardOpen(isKbOpen);
      setTimeout(() => scrollToBottom('smooth'), 150);
    };

    const handleScroll = () => {
      if (vv.offsetTop > 0) window.scrollTo(0, 0);
    };

    const initTimer = setTimeout(() => {
      baseHeight = vv.height;
    }, 300);

    vv.addEventListener('resize', handleResize);
    vv.addEventListener('scroll', handleScroll);

    return () => {
      clearTimeout(initTimer);
      vv.removeEventListener('resize', handleResize);
      vv.removeEventListener('scroll', handleScroll);
    };
  }, [scrollToBottom]);

  // ─── Update conversation meta after AI response ───────────────────────────
  useEffect(() => {
    if (!isLoading && pendingAIMsgIdRef.current === null && aiResponseCompletedRef.current) {
      aiResponseCompletedRef.current = false;
      const convId = activeConvIdRef.current;
      if (!convId || convId === GUEST_CONV_ID || isGuest) return;

      setMessages((prev) => {
        const lastAI = [...prev].reverse().find((m) => m.role === 'ai' && m.showActions);
        if (lastAI) {
          const firstUserMsg = isFirstUserMessageRef.current ? firstUserMsgRef.current : null;
          updateConversationMeta(convId, firstUserMsg, lastAI.content);
          if (isFirstUserMessageRef.current) {
            isFirstUserMessageRef.current = false;
            firstUserMsgRef.current = '';
          }
        }
        return prev;
      });
    }
  }, [isLoading, updateConversationMeta, isGuest]);

  // ─── Guest save prompt ────────────────────────────────────────────────────
  useEffect(() => {
    if (isGuest && showSavePrompt && !isLoading && !pendingAIMsgIdRef.current) {
      const promptId = `save-prompt-${Date.now()}`;
      setMessages((prev) => {
        if (prev.some((m) => m.id.startsWith('save-prompt'))) return prev;
        return [...prev, SAVE_PROMPT_MESSAGE(promptId)];
      });
    }
  }, [isGuest, showSavePrompt, isLoading]);

  // ─── Cleanup real-time subscription on unmount ────────────────────────────
  useEffect(() => {
    return () => {
      if (realtimeChannelRef.current) {
        supabase.removeChannel(realtimeChannelRef.current);
        realtimeChannelRef.current = null;
      }
    };
  }, [supabase]);

  // ─── Send message ─────────────────────────────────────────────────────────
  const sendMessage = useCallback(
    async (content: string, file?: File, actionType?: 'comfort' | 'understand' | 'solve') => {
      const trimmed = content.trim();
      if (!trimmed && !file) return;
      if (isSendingRef.current || isLoading) return;

      const convId = activeConvIdRef.current;
      if (!convId) return;

      isSendingRef.current = true;

      // Mark conversation as started — hide starters & mood check-in
      setConversationStarted(true);
      setShowMoodCheckIn(false);

      if (isGuest) {
        const newCount = guestExchangeCount + 1;
        setGuestExchangeCount(newCount);
        if (newCount >= GUEST_EXCHANGE_LIMIT && !showSavePrompt) {
          setShowSavePrompt(true);
        }
      }

      const mode = actionType || 'default';
      setCurrentMode(mode);

      const displayContent = trimmed || (file ? `📎 ${file.name}` : '');

      // Use a temp ID for optimistic UI — will be replaced with DB UUID after save
      const tempUserMsgId = `msg-user-${Date.now()}`;
      const userMsg: Message = {
        id: tempUserMsgId,
        role: 'user',
        content: displayContent,
        timestamp: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
      };

      setMessages((prev) => [...prev, userMsg]);
      setIsTyping(true);

      // Save user message to DB and replace temp ID with real DB UUID
      if (!isGuest) {
        const savedMsg = await saveMessage(convId, 'user', displayContent);
        if (savedMsg?.id) {
          // Replace temp ID with real DB ID so real-time dedup works on other devices
          setMessages((prev) =>
            prev.map((m) => (m.id === tempUserMsgId ? { ...m, id: savedMsg.id } : m))
          );
        }
      }

      const isFirstMsg = conversationHistoryRef.current.filter((h) => h.role === 'user').length === 0;
      if (isFirstMsg && !isGuest) {
        isFirstUserMessageRef.current = true;
        firstUserMsgRef.current = displayContent;
      }

      // Build user message content — multimodal if file attached
      let userMessageContent: any = trimmed || '';
      if (file) {
        try {
          const base64DataUri = await fileToBase64(file);
          const isImage = file.type.startsWith('image/');
          const contentParts: any[] = [];
          if (trimmed) contentParts.push({ type: 'text', text: trimmed });
          if (isImage) {
            contentParts.push({
              type: 'image_url',
              image_url: { url: base64DataUri, detail: 'auto' },
            });
          } else {
            contentParts.push({
              type: 'file',
              file: { file_data: base64DataUri, filename: file.name },
            });
          }
          userMessageContent = contentParts;
        } catch {
          toast.error('Could not read the attached file. Please try again.');
          isSendingRef.current = false;
          setIsTyping(false);
          setMessages((prev) => prev.filter((m) => m.id !== tempUserMsgId));
          return;
        }
      }

      // Use ref for always-current history (avoids stale closure bug)
      const currentHistory = conversationHistoryRef.current;
      const updatedHistory = [...currentHistory, { role: 'user', content: trimmed || displayContent }];
      conversationHistoryRef.current = updatedHistory;
      setConversationHistory(updatedHistory);

      const systemPrompt = buildSystemPrompt(mode, moodContext, journalContext, aiWarmth, accountabilityContext);

      // Build API messages — use multimodal content for the latest user turn if file attached
      const historyForApi = currentHistory.map((h) => ({ role: h.role, content: h.content }));
      const apiMessages = [
        { role: 'system', content: systemPrompt },
        ...historyForApi,
        { role: 'user', content: userMessageContent },
      ];

      const aiMsgId = `msg-ai-${Date.now()}`;
      pendingAIMsgIdRef.current = aiMsgId;
      persistedMsgIdRef.current = null; // reset so this new message can be persisted
      pendingAIContentRef.current = '';

      const placeholderMsg: Message = {
        id: aiMsgId,
        role: 'ai',
        content: '',
        timestamp: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
        showActions: false,
      };
      setMessages((prev) => [...prev, placeholderMsg]);

      sendAIMessage(apiMessages, { max_completion_tokens: 500 });
    },
    [
      moodContext,
      journalContext,
      aiWarmth,
      accountabilityContext,
      isLoading,
      sendAIMessage,
      saveMessage,
      isGuest,
      guestExchangeCount,
      showSavePrompt,
    ]
  );

  const handleAction = useCallback(
    (type: 'comfort' | 'understand' | 'solve') => {
      const labels = {
        comfort: '💜 Comfort me',
        understand: '🪞 Help me understand',
        solve: '✨ Help me solve it',
      };
      sendMessage(labels[type], type);
    },
    [sendMessage]
  );

  const handleSuggested = useCallback((text: string) => sendMessage(text), [sendMessage]);

  const handleRegenerate = useCallback(() => {
    // Find the last user message and resend it
    const lastUserMsg = [...messages].reverse().find((m) => m.role === 'user');
    if (!lastUserMsg || isLoading) return;
    // Remove the last AI message and resend
    setMessages((prev) => {
      const lastAIIdx = [...prev].reverse().findIndex((m) => m.role === 'ai');
      if (lastAIIdx === -1) return prev;
      const realIdx = prev.length - 1 - lastAIIdx;
      return prev.slice(0, realIdx);
    });
    // Update both ref and state when removing last assistant message
    const currentHistory = conversationHistoryRef.current;
    const lastAssistantIdx = [...currentHistory].reverse().findIndex((m) => m.role === 'assistant');
    if (lastAssistantIdx !== -1) {
      const realIdx = currentHistory.length - 1 - lastAssistantIdx;
      const trimmedHistory = currentHistory.slice(0, realIdx);
      conversationHistoryRef.current = trimmedHistory;
      setConversationHistory(trimmedHistory);
    }
    setTimeout(() => {
      sendMessage(lastUserMsg.content);
    }, 100);
  }, [messages, isLoading, sendMessage]);

  const handleRemember = useCallback(
    async (messageId: string, content: string) => {
      if (!user) return;
      setRememberedMessages((prev) => new Set([...prev, messageId]));
      toast.success('Clingy will remember this 💜', { duration: 2000 });
      // Persist to the memories table
      try {
        await supabase.from('memories').insert({
          user_id: user.id,
          content,
          category: 'Chat',
        });
      } catch {
        // Silently ignore if insert fails
      }
    },
    [user, supabase]
  );

  const handleMoodCheckIn = useCallback(
    (mood: string) => {
      setShowMoodCheckIn(false);
      sendMessage(`I'm feeling ${mood} right now.`);
    },
    [sendMessage]
  );

  const lastMessage = messages[messages.length - 1];
  const showActions =
    !isTyping && !isLoading && lastMessage?.role === 'ai' && lastMessage?.showActions;

  return (
    <div
      className="flex bg-background overflow-hidden"
      style={{
        height: '100dvh',
        minHeight: '-webkit-fill-available',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overscrollBehavior: 'none',
        touchAction: 'none',
      }}
    >
      {!isGuest && (
        <ConversationSidebar
          conversations={conversations}
          activeId={activeConvId || ''}
          onSelect={(id) => selectConversation(id)}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          onNewChat={createNewConversation}
          onRename={handleRenameConversation}
          onDelete={handleDeleteConversation}
          isLoading={isLoadingConvs}
        />
      )}

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden" style={{ height: '100%' }}>
        {/* Fixed header */}
        <ChatHeader
          onMenuClick={() => setSidebarOpen(true)}
          onNewChat={isGuest ? () => {} : createNewConversation}
          isGuest={isGuest}
        />

        {/* Messages — only this scrolls */}
        <div
          ref={scrollContainerRef}
          className="flex-1 overflow-y-auto px-4 py-4 scrollbar-hide"
          style={{
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
            touchAction: 'pan-y',
          }}
        >
          {isLoadingMessages ? (
            <div className="flex items-center justify-center h-full">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <MessageList
              messages={messages}
              isTyping={isTyping && !response}
              onAction={handleAction}
              showActions={showActions}
              scrollRef={scrollContainerRef}
              onRegenerate={handleRegenerate}
              onRemember={handleRemember}
            />
          )}
          <div ref={messagesEndRef} className="h-4" />
        </div>

        {/* Guest save prompt banner */}
        {isGuest && showSavePrompt && !isLoading && (
          <div className="px-4 py-3 bg-primary/8 border-t border-primary/20 flex-shrink-0">
            <div className="max-w-lg mx-auto flex items-center justify-between gap-3">
              <p className="text-xs text-foreground font-medium leading-relaxed flex-1">
                Save your conversations and let Clingy remember you 💜
              </p>
              <button
                onClick={() => router.push('/onboarding-sign-up-login-screen')}
                className="flex-shrink-0 px-4 py-2 rounded-xl lavender-gradient text-white text-xs font-bold transition-all active:scale-95"
              >
                Save my conversations
              </button>
            </div>
          </div>
        )}

        {/* Mood check-in — shown at start of new conversation */}
        {!isGuest && showMoodCheckIn && !conversationStarted && (
          <MoodCheckIn
            onSelect={handleMoodCheckIn}
            onDismiss={() => setShowMoodCheckIn(false)}
          />
        )}

        {/* Conversation starters — shown before first message */}
        {!conversationStarted && !isTyping && !isLoading && (
          <ConversationStarters
            onSelect={handleSuggested}
            visible={!conversationStarted}
          />
        )}

        {/* Suggested replies — shown after conversation starts */}
        {conversationStarted && !isTyping && !isLoading && (
          <SuggestedReplies suggestions={SUGGESTED} onSelect={handleSuggested} />
        )}

        {/* Composer — always visible above keyboard */}
        <div className="flex-shrink-0">
          <MessageComposer
            onSend={(content, file) => sendMessage(content, file)}
            isTyping={isTyping || isLoading}
            onStop={abortAI}
            keyboardOpen={keyboardOpen}
            isGuest={isGuest}
          />
        </div>
      </div>

      {!isGuest && !keyboardOpen && <BottomNav />}
    </div>
  );
}